/**
 * AVTR Cart Drawer — Alpine.js Component
 * Registra el store global `avtrCart` y coordina
 * el drawer lateral con la Shopify AJAX API.
 */

document.addEventListener('alpine:init', () => {
  Alpine.data('avtrCart', () => ({

    /* ── State ─────────────────────────────────── */
    isCartOpen:            false,
    cart:                  null,
    loading:               false,
    updatingItemId:        null,
    removingItemId:        null,
    freeShippingThreshold: 30000000,   // cents — $300.000 COP equivalente

    /* Regalo: 2 prendas (jean/dril/hoodie) → el cliente elige camiseta o top y talla */
    gift:           null,     // datos de snippets/avtr-gift-data.liquid
    giftOpen:       false,
    giftStep:       'box',    // 'box' (animación) → 'pick' (para quién, prenda y talla)
    giftFor:        null,     // 'Hombre' | 'Mujer'
    giftPick:       null,     // id de la prenda elegida (avtr-gift-data → garments)
    giftSize:       null,
    giftStock:      null,     // { handle: ['M', 'L'] } tallas con stock de las prendas de regalo
    giftImg:        {},       // { handle: url } foto de cada prenda de regalo
    giftAdding:     false,
    giftError:      '',
    giftDismissed:  false,
    giftNotice:     '',

    /* ── Init ──────────────────────────────────── */
    async init() {
      console.log('[AVTR Cart Drawer JS] Initializing Alpine avtrCart store...');
      this.loadGift();
      // Hidratamos el cart desde la API sin esperar a que el usuario abra el drawer
      await this.refreshCart();

      // Escuchar eventos externos (product-form.js, quick-add, etc.)
      document.addEventListener('cart:refresh', async () => {
        console.log('[AVTR Cart Drawer JS] Received cart:refresh event.');
        await this.refreshCart();
      });
      document.addEventListener('cart:open', () => {
        console.log('[AVTR Cart Drawer JS] Received cart:open event. Opening drawer (setting isCartOpen = true).');
        this.isCartOpen = true;
      });
      document.addEventListener('cart:close', () => {
        console.log('[AVTR Cart Drawer JS] Received cart:close event. Closing drawer.');
        this.isCartOpen = false;
      });

      // Bloqueo de scroll en body
      this.$watch('isCartOpen', value => {
        console.log(`[AVTR Cart Drawer JS] isCartOpen watch triggered: ${value}`);
        document.body.classList.toggle('avtr-cart-open', value);
        document.body.classList.toggle('js-cart-open', value);
        document.body.classList.toggle('cart-is-active', value);
        if (value) {
          this.checkGift();
          // Devolver foco al botón de cierre para accesibilidad
          this.$nextTick(() => {
            const closeBtn = document.querySelector('#avtr-cart-drawer .avtr-cart-close');
            if (closeBtn) closeBtn.focus();
          });
        }
      });
    },

    /* ── Fetch cart.js ─────────────────────────── */
    async refreshCart() {
      console.log('[AVTR Cart Drawer JS] Fetching cart JSON from API...');
      this.loading = true;
      try {
        const cartUrl = (window.routes && window.routes.cart_url) ? window.routes.cart_url : '/cart';
        const res  = await fetch(cartUrl + '.js');
        this.cart  = await res.json();
        console.log('[AVTR Cart Drawer JS] Cart state loaded successfully:', this.cart);
        this.updateHeaderBubble();
        this.checkGift();
      } catch (e) {
        console.error('[AVTR Cart] Error refreshing cart:', e);
      } finally {
        this.loading = false;
      }
    },

    /* ── Update quantity (Optimistic UI) ─────────── */
    async updateQuantity(key, quantity) {
      if (this.updatingItemId) return;   // debounce
      this.updatingItemId = key;

      /* Snapshot para rollback */
      const prevCart = JSON.parse(JSON.stringify(this.cart));

      /* Optimistic update inmediato */
      const idx = this.cart.items.findIndex(i => i.key === key);
      if (idx > -1) {
        if (quantity === 0) {
          this.cart.item_count -= this.cart.items[idx].quantity;
          this.cart.total_price -= this.cart.items[idx].final_line_price;
          this.cart.items.splice(idx, 1);
        } else {
          const item  = this.cart.items[idx];
          const diff  = quantity - item.quantity;
          item.quantity         = quantity;
          item.final_line_price = item.final_price * quantity;
          this.cart.total_price += diff * item.final_price;
          this.cart.item_count  += diff;
        }
      }
      this.updateHeaderBubble();

      /* Sync con API */
      try {
        const res     = await fetch(window.routes.cart_change_url + '.js', {
          method:  'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body:    JSON.stringify({ id: key, quantity }),
        });
        const newCart = await res.json();
        /* 422 (sin stock, etc.) devuelve {status, message}, no un cart */
        if (!res.ok || !Array.isArray(newCart.items)) {
          throw new Error(newCart.description || newCart.message || res.status);
        }
        this.cart     = newCart;
        this.updateHeaderBubble();
      } catch (e) {
        console.error('[AVTR Cart] Error updating quantity:', e);
        this.cart = prevCart;                   // rollback
        this.updateHeaderBubble();
        /* Shopify puede ajustar la cantidad aunque responda 422 (stock máx.) */
        await this.refreshCart();
      } finally {
        this.updatingItemId = null;
      }
      this.checkGift();
    },

    /* ── Remove item (with exit animation) ────────── */
    async removeItem(key) {
      this.removingItemId = key;
      /* Esperar animación CSS de salida */
      await new Promise(r => setTimeout(r, 320));
      this.removingItemId = null;
      await this.updateQuantity(key, 0);
    },

    /* ── Regalo con 2 prendas (el cliente lo elige) ── */
    /* El cliente no elige la prenda: solo dice para quién es y la talla.
       Se guarda como atributo del carrito ("Regalo sorpresa") y Steven
       escoge la prenda al despachar. No se agrega ningún producto. */
    GIFT_ATTR: 'Regalo sorpresa',

    loadGift() {
      try {
        const el = document.getElementById('avtr-gift-data');
        if (!el) return;
        const data = JSON.parse(el.textContent);
        if (Date.now() >= Date.parse(data.ends)) return;   // ya terminó
        this.gift = data;
      } catch (e) {
        console.error('[AVTR Gift] Datos inválidos:', e);
      }
      try { this.giftDismissed = sessionStorage.getItem('avtrGiftDismissed') === '1'; } catch (e) {}
      if (this.gift) this.loadGiftStock();
    },

    /* Reglas (Steven 2026-10-26):
       - Mujer: 2 jeans/driles de mujer, o 1 jean/dril de mujer + 1 hoodie (Steven 2026-10-27).
       - Hombre: 1 jean/dril de hombre + 1 hoodie.
       - 2 hoodies: también aplica; se pregunta para quién es (Steven 2026-10-27). */
    giftCount(kind) {
      if (!this.gift || !this.cart) return 0;
      return this.cart.items.filter(i => {
        if (!this.gift.qualifying.includes(i.handle)) return false;
        const hoodie = i.handle.startsWith('hoodie-');
        const mujer  = i.handle.includes('-mujer-');
        return kind === 'hoodie' ? hoodie : kind === 'mujer' ? mujer : (!hoodie && !mujer);
      }).reduce((n, i) => n + i.quantity, 0);
    },

    get giftWomen() {
      const m = this.giftCount('mujer');
      return m >= 2 || (m >= 1 && this.giftCount('hoodie') >= 1);
    },
    get giftMen()   { return this.giftCount('hombre') >= 1 && this.giftCount('hoodie') >= 1; },
    get giftHoodies() { return this.giftCount('hoodie') >= 2; },
    /* Hay que preguntar "Para" si cumple las dos reglas o si solo lleva hoodies */
    get giftAskFor() { return (this.giftWomen && this.giftMen) || (this.giftHoodies && !this.giftWomen && !this.giftMen); },

    get giftHint() {
      if (!this.gift || !this.cart || this.giftEligible) return '';
      const m = this.giftCount('mujer'), h = this.giftCount('hombre'), hd = this.giftCount('hoodie');
      if (h >= 1 && hd === 0 && m === 0) return 'Agrega un hoodie y eliges tu regalo gratis.';
      if (hd === 1 && h === 0 && m === 0) return 'Agrega un jean u otro hoodie y eliges tu regalo gratis.';
      if (m === 1) return 'Agrega otro jean de mujer o un hoodie y eliges tu regalo gratis.';
      return '';
    },

    /* Stock en vivo de las prendas de regalo (para mostrar solo tallas disponibles) */
    async loadGiftStock() {
      const handles = [...new Set((this.gift.garments || []).flatMap(g => g.handles))];
      const stock = {};
      await Promise.all(handles.map(async (h) => {
        try {
          const p = await (await fetch('/products/' + h + '.js')).json();
          stock[h] = p.variants.filter(v => v.available).map(v => v.option1);
          if (p.featured_image) this.giftImg[h] = p.featured_image + (p.featured_image.includes('?') ? '&' : '?') + 'width=300';
        } catch (e) { stock[h] = null; }   // si falla, no se descarta la prenda
      }));
      this.giftStock = stock;
    },

    giftHas(g, size) {
      if (!this.giftStock) return true;
      return g.handles.some(h => !this.giftStock[h] || this.giftStock[h].includes(size));
    },

    /* Opciones de regalo para quién es, solo con tallas disponibles */
    get giftOptions() {
      if (!this.gift || !this.gift.garments || !this.giftFor) return [];
      return this.gift.garments
        .filter(g => g.para.includes(this.giftFor))
        .map(g => ({ id: g.id, name: g.name, fit: g.fit, tip: g.tip, m: g.sizes,
                     img: this.giftImg[g.handles[0]] || '',
                     sizes: Object.keys(g.sizes).filter(sz => this.giftHas(g, sz)) }))
        .filter(g => g.sizes.length);
    },

    get giftChosen() {
      return this.giftOptions.find(g => g.id === this.giftPick) || null;
    },

    get giftSizes() {
      return this.giftChosen ? this.giftChosen.sizes : [];
    },

    pickGiftFor(v) { this.giftFor = v; this.giftPick = null; this.giftSize = null; this.giftError = ''; },
    pickGift(id)   { this.giftPick = id; this.giftSize = null; this.giftError = ''; },

    get giftSizeOk() {
      return !!this.giftSize && this.giftSizes.includes(this.giftSize);
    },

    get giftValue() {
      return (this.cart && this.cart.attributes && this.cart.attributes[this.GIFT_ATTR]) || '';
    },

    get giftInCart() {
      return this.giftValue.startsWith('Sí');
    },

    get giftEligible() {
      return !!this.gift && (this.giftWomen || this.giftMen || this.giftHoodies);
    },

    async setGiftAttr(value) {
      const res = await fetch('/cart/update.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body:    JSON.stringify({ attributes: { [this.GIFT_ATTR]: value } }),
      });
      if (!res.ok) throw new Error(res.status);
      this.cart = await res.json();
    },

    async checkGift() {
      if (!this.gift || !this.cart || this.giftAdding) return;

      /* Si ya no cumple, se cierra y se borra el regalo del pedido */
      if (!this.giftEligible) {
        this.giftOpen = false;
        if (this.giftValue) {
          if (this.giftInCart) this.giftNotice = 'Quitamos tu regalo: va con jean + hoodie, 2 hoodies o 2 jeans de mujer.';
          try { await this.setGiftAttr(''); } catch (e) {}
        }
        return;
      }

      /* Si solo cumple una regla, ya sabemos para quién es */
      if (this.giftWomen !== this.giftMen) {
        const f = this.giftWomen ? 'Mujer' : 'Hombre';
        if (this.giftFor !== f) this.pickGiftFor(f);
      }
      /* Si el regalo guardado era para el otro (cambió el carrito), se vuelve a preguntar la talla */
      if (this.giftInCart && this.giftWomen !== this.giftMen && !this.giftValue.includes(this.giftFor)) {
        try { await this.setGiftAttr(''); } catch (e) {}
      }
      if (this.giftInCart) { this.giftOpen = false; return; }
      if (this.isCartOpen && !this.giftOpen && !this.giftDismissed) this.openGift();
    },

    openGift() {
      this.giftError  = '';
      this.giftNotice = '';
      this.giftOpen   = true;
      const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.giftStep = reduce ? 'pick' : 'box';
      if (!reduce) setTimeout(() => { this.giftStep = 'pick'; }, 1500);
    },

    async addGift() {
      if (!this.giftFor || !this.giftChosen || !this.giftSizeOk || this.giftAdding) return;
      this.giftAdding = true;
      this.giftError  = '';
      try {
        await this.setGiftAttr('Sí · ' + this.giftFor + ' · ' + this.giftChosen.name + ' · Talla ' + this.giftSize);
        this.giftOpen = false;
      } catch (e) {
        console.error('[AVTR Gift] No se pudo guardar:', e);
        this.giftError = 'No pudimos guardar tu regalo. Intenta de nuevo.';
      } finally {
        this.giftAdding = false;
      }
    },

    async declineGift() {
      this.giftOpen      = false;
      this.giftDismissed = true;
      try { sessionStorage.setItem('avtrGiftDismissed', '1'); } catch (e) {}
      try { await this.setGiftAttr('No lo quiere'); } catch (e) {}
    },

    reopenGift() {
      this.giftDismissed = false;
      try { sessionStorage.removeItem('avtrGiftDismissed'); } catch (e) {}
      this.openGift();
    },

    /* ── Header bubble ─────────────────────────── */
    updateHeaderBubble() {
      const bubble = document.querySelector('.cart-count');
      if (!bubble) return;
      const count = this.cart ? this.cart.item_count : 0;
      bubble.textContent = count;
      bubble.style.display = count > 0 ? 'inline-flex' : 'none';
    },

    /* ── Thumbnail ─────────────────────────────── */
    // Sin loading="lazy": avtr-design-system.css oculta img lazy hasta que
    // avtr-scroll-reveal.js les pone .loaded, y eso no ocurre con nodos de Alpine.
    thumbUrl(item) {
      const img = item.featured_image;
      const src = (img && (typeof img === 'string' ? img : img.url)) || item.image || '';
      if (!src) return '';
      return src + (src.includes('?') ? '&' : '?') + 'width=160';
    },

    /* ── Format price ──────────────────────────── */
    formatMoney(cents) {
      if (cents == null) return '';
      const format = window.avtrMoneyFormat || ((window.currencySymbol || '$') + '{{amount}}');
      const placeholder = format.match(/\{\{\s*(\w+)\s*\}\}/);
      if (!placeholder) return format;

      // Mismos placeholders que el filtro `money` de Shopify
      const fmt = (precision, thousands, decimal) => {
        const [int, dec] = (cents / 100).toFixed(precision).split('.');
        return int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (dec ? decimal + dec : '');
      };
      const formatters = {
        amount:                                   () => fmt(2, ',', '.'),
        amount_no_decimals:                       () => fmt(0, ',', '.'),
        amount_with_comma_separator:              () => fmt(2, '.', ','),
        amount_no_decimals_with_comma_separator:  () => fmt(0, '.', ','),
        amount_with_apostrophe_separator:         () => fmt(2, "'", '.'),
        amount_no_decimals_with_space_separator:  () => fmt(0, ' ', ','),
        amount_with_space_separator:              () => fmt(2, ' ', ','),
        amount_with_period_and_space_separator:   () => fmt(2, ' ', '.'),
      };
      const value = (formatters[placeholder[1]] || formatters.amount)();
      return format.replace(placeholder[0], value);
    },

  }));
});
